import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

// Thresholds for second-memory rate colour coding
const SECOND_MEMORY_RATE = { green: 60, amber: 30 };

// Canonical display order for memory types
const TYPE_ORDER = ["voice", "written", "photo", "drawing", "shortie", "measurement", "unknown"];

// Canonical day order (Sun first — matches PostgreSQL dow 0–6)
const DOW_ORDER = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TYPE_LABELS: Record<string, string> = {
  voice:       "Voice",
  written:     "Written",
  photo:       "Photo",
  drawing:     "Drawing",
  shortie:     "Shortie",
  measurement: "Measurement",
  unknown:     "Unknown",
};

interface MemoriesData {
  total_memories: number;
  new_this_week: number;
  new_this_month: number;
  avg_per_active_user: number;
  second_memory_rate: number;
  by_type: Record<string, number>;
  by_type_this_month: Record<string, number>;
  by_day_of_week: Record<string, number>;
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function TypeTable({
  data,
  total,
  title,
}: {
  data: Record<string, number>;
  total: number;
  title: string;
}) {
  const sorted = TYPE_ORDER.filter((t) => data[t] !== undefined).concat(
    Object.keys(data).filter((t) => !TYPE_ORDER.includes(t))
  );

  const pct = (n: number) =>
    total === 0 ? "0%" : `${Math.round((n / total) * 100)}%`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </p>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Count</th>
            <th>%</th>
            <th style={{ width: "35%" }}>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((type) => {
            const count = data[type] ?? 0;
            return (
              <tr key={type}>
                <td className="font-mono text-gray-700">
                  {TYPE_LABELS[type] ?? type}
                </td>
                <td className="text-gray-900 font-medium">{count}</td>
                <td className="text-gray-500">{pct(count)}</td>
                <td>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded-full"
                      style={{ width: pct(count) }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function MemoriesDashboard() {
  const [data, setData] = useState<MemoriesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase.rpc(
      "get_memories_dashboard"
    );
    if (rpcError) {
      setError(rpcError.message);
      setLoading(false);
      return;
    }
    setData(result as unknown as MemoriesData);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function formatLastUpdated(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 minute ago";
    return `${diffMin} minutes ago`;
  }

  function rateColor(rate: number): string {
    if (rate >= SECOND_MEMORY_RATE.green) return "text-green-600";
    if (rate >= SECOND_MEMORY_RATE.amber) return "text-amber-500";
    return "text-red-500";
  }

  // Totals for type tables
  const totalAllTime = data
    ? Object.values(data.by_type).reduce((a, b) => a + b, 0)
    : 0;
  const totalThisMonth = data
    ? Object.values(data.by_type_this_month).reduce((a, b) => a + b, 0)
    : 0;

  // Day-of-week bars
  const dowEntries = data
    ? DOW_ORDER.map((day) => ({ day, count: data.by_day_of_week[day] ?? 0 }))
    : [];
  const dowMax = dowEntries.reduce((m, e) => Math.max(m, e.count), 1);

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Memories</h1>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              Last updated: {formatLastUpdated(lastUpdated)}
            </p>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">Failed to load</p>
          <p className="text-xs text-red-500 mt-0.5">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 p-5 h-24 animate-pulse"
              >
                <div className="h-2 bg-gray-100 rounded w-16 mb-3" />
                <div className="h-8 bg-gray-100 rounded w-12" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data */}
      {data && (
        <div className="space-y-6">
          {/* Row 1 — headline numbers */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label="Total memories"
              value={data.total_memories}
              sub="all time"
            />
            <StatCard label="New this week" value={data.new_this_week} />
            <StatCard label="New this month" value={data.new_this_month} />
            <StatCard
              label="Avg per active user"
              value={data.avg_per_active_user ?? 0}
              sub="last 30 days"
            />
          </div>

          {/* Row 2 — second memory rate */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Second memory rate
                </p>
                <p
                  className={`text-5xl font-bold ${rateColor(data.second_memory_rate ?? 0)}`}
                >
                  {data.second_memory_rate ?? 0}%
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Users who created 2+ memories
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Core loop indicator</p>
              </div>
              <div className="text-right text-xs text-gray-400 space-y-1 pt-1">
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />
                  ≥{SECOND_MEMORY_RATE.green}% healthy
                </p>
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />
                  {SECOND_MEMORY_RATE.amber}–{SECOND_MEMORY_RATE.green - 1}% watch
                </p>
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1" />
                  &lt;{SECOND_MEMORY_RATE.amber}% at risk
                </p>
              </div>
            </div>
          </div>

          {/* Row 3 — type breakdown */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Memory type breakdown
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TypeTable
                data={data.by_type}
                total={totalAllTime}
                title="All time"
              />
              <TypeTable
                data={data.by_type_this_month}
                total={totalThisMonth}
                title="This month"
              />
            </div>
          </div>

          {/* Row 4 — day of week activity */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Activity by day of week
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
              {dowEntries.map(({ day, count }) => (
                <div key={day} className="flex items-center gap-3">
                  <span className="w-8 text-xs font-medium text-gray-500 text-right shrink-0">
                    {day}
                  </span>
                  <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded transition-all"
                      style={{
                        width: `${Math.round((count / dowMax) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="w-10 text-xs text-gray-500 shrink-0">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
